import React, { useMemo } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { ChevronLeft, ChevronRight, Printer, Phone, Edit2, Trash2 } from 'lucide-react';

const STATUS_CYCLE = { '': 'present', present: 'absent', absent: 'half_day', half_day: '' };
const STATUS_LABEL = { present: 'P', absent: 'A', half_day: 'H' };
const STATUS_BG = {
  present: 'bg-green-600 text-white',
  absent: 'bg-red-600 text-white',
  half_day: 'bg-amber-500 text-white',
};

const todayISO = () => new Date().toISOString().split('T')[0];

const buildMonthDates = (yyyymm) => {
  const [y, m] = yyyymm.split('-').map(Number);
  const dim = new Date(y, m, 0).getDate();
  const days = [];
  for (let d = 1; d <= dim; d++) {
    const ds = `${yyyymm}-${String(d).padStart(2, '0')}`;
    const dt = new Date(ds);
    const dow = dt.getDay(); // 0=Sun..6=Sat
    days.push({
      date: ds,
      day: d,
      dow,
      isWeekend: dow === 0,
      weekday: dt.toLocaleDateString('en-IN', { weekday: 'short' }).slice(0, 1),
    });
  }
  return days;
};

const MonthlyAttendanceGrid = ({
  staff,
  attendance,
  selectedMonth,
  onChangeMonth,
  setSelectedMonth,
  onCycleCell,
  onPrint,
  onEditStaff,
  onDeleteStaff,
  onProfileClick,
  pendingCells = {},
}) => {
  const monthDays = useMemo(() => buildMonthDates(selectedMonth), [selectedMonth]);
  const today = todayISO();

  const monthName = new Date(selectedMonth + '-01').toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
  });

  // Build a quick lookup map: `${staff_id}|${date}` -> status
  const attMap = useMemo(() => {
    const m = {};
    attendance.forEach((a) => { m[`${a.staff_id}|${a.date}`] = a.status; });
    return m;
  }, [attendance]);

  const getStatus = (sid, date) => {
    const key = `${sid}|${date}`;
    if (key in pendingCells) return pendingCells[key];
    return attMap[key] || '';
  };

  const summarise = (sid) => {
    let p = 0, a = 0, h = 0;
    monthDays.forEach((md) => {
      const s = getStatus(sid, md.date);
      if (s === 'present') p++;
      else if (s === 'absent') a++;
      else if (s === 'half_day') h++;
    });
    return { p, h, a, working: p + h * 0.5 };
  };

  return (
    <div className="space-y-3" data-testid="monthly-attendance-grid">
      {/* Month nav + actions */}
      <Card className="p-3 border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onChangeMonth(-1)} data-testid="prev-month-btn">
            <ChevronLeft className="h-4 w-4 mr-1" /> Prev
          </Button>
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="h-9 rounded-md border border-slate-200 px-2 text-sm font-medium text-slate-900"
            data-testid="month-selector"
          />
          <Button variant="outline" size="sm" onClick={() => onChangeMonth(1)} data-testid="next-month-btn">
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
          <span className="ml-3 text-sm text-slate-500">{monthName} · {monthDays.length} days</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 hidden md:inline">Click a cell to cycle: empty → P → A → H → empty · auto-saves</span>
          <Button size="sm" variant="outline" onClick={onPrint} data-testid="print-monthly-btn">
            <Printer className="h-4 w-4 mr-1" /> Print
          </Button>
        </div>
      </Card>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-slate-600 px-2">
        <span className="flex items-center gap-1">
          <span className="w-5 h-5 rounded bg-green-600 text-white flex items-center justify-center font-bold">P</span> Present
        </span>
        <span className="flex items-center gap-1">
          <span className="w-5 h-5 rounded bg-red-600 text-white flex items-center justify-center font-bold">A</span> Absent
        </span>
        <span className="flex items-center gap-1">
          <span className="w-5 h-5 rounded bg-amber-500 text-white flex items-center justify-center font-bold">H</span> Half Day
        </span>
        <span className="flex items-center gap-1 text-slate-400">
          <span className="w-5 h-5 rounded border border-slate-200" /> Empty
        </span>
      </div>

      {/* Spreadsheet grid */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto" data-testid="monthly-grid-scroll">
          <table className="border-collapse text-xs" style={{ minWidth: '100%' }}>
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th
                  className="sticky left-0 bg-slate-50 px-3 py-2 text-left font-medium text-slate-700 border-r border-slate-200 z-20"
                  style={{ minWidth: 200 }}
                >
                  Staff
                </th>
                {monthDays.map((md) => {
                  const isToday = md.date === today;
                  return (
                    <th
                      key={md.date}
                      className={`px-1 py-1 text-center font-normal border-r border-slate-100 ${
                        isToday ? 'bg-blue-100 text-blue-700 font-semibold' : md.isWeekend ? 'bg-rose-50 text-rose-700' : 'text-slate-600'
                      }`}
                      style={{ minWidth: 28 }}
                    >
                      <div className="text-[9px] leading-none">{md.weekday}</div>
                      <div className="text-[11px] font-bold">{md.day}</div>
                    </th>
                  );
                })}
                <th className="sticky right-0 bg-slate-50 px-3 py-2 text-center font-medium text-slate-700 border-l border-slate-200 z-20" style={{ minWidth: 140 }}>
                  Summary
                </th>
              </tr>
            </thead>
            <tbody>
              {staff.length === 0 ? (
                <tr>
                  <td colSpan={monthDays.length + 2} className="px-4 py-12 text-center text-slate-500">
                    No staff members added yet.
                  </td>
                </tr>
              ) : (
                staff.map((s) => {
                  const sum = summarise(s.id);
                  return (
                    <tr key={s.id} className="hover:bg-slate-50/50 border-b border-slate-100">
                      {/* Sticky staff cell */}
                      <td
                        className="sticky left-0 bg-white hover:bg-slate-50 px-3 py-1.5 border-r border-slate-200 z-10"
                        style={{ minWidth: 200 }}
                      >
                        <div className="flex items-center justify-between gap-1">
                          <button
                            type="button"
                            onClick={() => onProfileClick(s)}
                            className="text-left flex-1 min-w-0 hover:text-blue-600"
                            data-testid={`staff-name-${s.id}`}
                          >
                            <div className="font-medium text-slate-900 truncate text-[13px]">{s.name}</div>
                            <div className="text-[10px] text-slate-500 flex items-center gap-1 truncate">
                              <Phone className="h-2.5 w-2.5 flex-shrink-0" />
                              <span className="truncate">{s.phone}</span>
                              {s.role && <span className="text-blue-600 ml-1 truncate">· {s.role}</span>}
                            </div>
                          </button>
                          <div className="flex flex-shrink-0">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6"
                              onClick={() => onEditStaff(s)}
                              data-testid={`edit-staff-${s.id}`}
                            >
                              <Edit2 className="h-3 w-3" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6 text-red-500"
                              onClick={() => onDeleteStaff(s.id)}
                              data-testid={`delete-staff-${s.id}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </td>

                      {/* Day cells */}
                      {monthDays.map((md) => {
                        const status = getStatus(s.id, md.date);
                        const isToday = md.date === today;
                        const cellKey = `${s.id}|${md.date}`;
                        const isPending = cellKey in pendingCells;
                        const next = STATUS_CYCLE[status] || '';
                        const cls = status
                          ? STATUS_BG[status]
                          : md.isWeekend
                            ? 'bg-rose-50/30 hover:bg-rose-50'
                            : 'bg-white hover:bg-slate-100';
                        return (
                          <td
                            key={md.date}
                            className={`p-0 border-r border-slate-100 ${isToday ? 'bg-blue-50/30' : ''}`}
                          >
                            <button
                              type="button"
                              onClick={() => onCycleCell(s.id, md.date, status, next)}
                              className={`w-7 h-8 m-0.5 rounded text-[11px] font-bold flex items-center justify-center transition ${cls} ${
                                isPending ? 'ring-2 ring-blue-400 ring-offset-0' : ''
                              }`}
                              title={`${md.date} ${status ? `· ${status}` : ''}`}
                              data-testid={`cell-${s.id}-${md.date}`}
                            >
                              {STATUS_LABEL[status] || ''}
                            </button>
                          </td>
                        );
                      })}

                      {/* Sticky summary */}
                      <td
                        className="sticky right-0 bg-white px-3 py-1.5 border-l border-slate-200 z-10"
                        style={{ minWidth: 140 }}
                      >
                        <div className="flex items-center justify-around gap-1 text-[11px]" data-testid={`summary-${s.id}`}>
                          <div className="flex items-center gap-1 text-green-700">
                            <span className="w-4 h-4 rounded bg-green-600 text-white flex items-center justify-center text-[9px] font-bold">P</span>
                            <span className="font-semibold tabular-nums">{sum.p}</span>
                          </div>
                          <div className="flex items-center gap-1 text-amber-700">
                            <span className="w-4 h-4 rounded bg-amber-500 text-white flex items-center justify-center text-[9px] font-bold">H</span>
                            <span className="font-semibold tabular-nums">{sum.h}</span>
                          </div>
                          <div className="flex items-center gap-1 text-red-700">
                            <span className="w-4 h-4 rounded bg-red-600 text-white flex items-center justify-center text-[9px] font-bold">A</span>
                            <span className="font-semibold tabular-nums">{sum.a}</span>
                          </div>
                        </div>
                        <div className="text-[9px] text-slate-400 text-center mt-0.5">
                          {sum.working} working
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-xs text-slate-400 text-center">
        Tip: Cells with weekend dates are highlighted in pink. Today's column has a blue background.
      </p>
    </div>
  );
};

export default MonthlyAttendanceGrid;
